import React from "react";
import { Button, Grid, Typography, Box, Chip } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import {
  useGetBranchTallyDetailQuery,
  useAddParticipantsMutation,
} from "store/services/branchTallyApi";
import { useLazySearchUsersQuery } from "store/services/userApi";
import { useAuth } from "hooks";


const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const BranchTallyUpdateScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const listPath = "/app/crm/branch-tally/list";
  const { viewEntity } = useAuth();
  const entity = viewEntity?.entity?._id;

  const [newParticipants, setNewParticipants] = React.useState([]);
  const [userSearch, setUserSearch] = React.useState("");
  const debouncedUserSearch = useDebounce(userSearch, 400);

  const { data: tally, isLoading } = useGetBranchTallyDetailQuery(id);
  const [triggerUsersDDL, { data: userOptions = [] }] =
    useLazySearchUsersQuery();
  const [addParticipants, { isLoading: saving }] = useAddParticipantsMutation();

  React.useEffect(() => {
    if (!entity) return;
    if (debouncedUserSearch.trim().length < 3) return;
    triggerUsersDDL({ q: debouncedUserSearch, entity });
  }, [debouncedUserSearch, triggerUsersDDL, entity]);

  const existingParticipantIds = React.useMemo(
    () => new Set((tally?.participants ?? []).map((p) => p._id?.toString())),
    [tally?.participants],
  );

  const availableUsers = userOptions.filter(
    (u) => !existingParticipantIds.has(u._id?.toString()),
  );

  const handleSave = async () => {
    if (newParticipants.length === 0) {
      toast.warning("Please select at least one new participant.");
      return;
    }
    try {
      await addParticipants({
        id,
        participants: newParticipants.map((u) => u._id),
      }).unwrap();
      toast.success("Participants added successfully!");
      navigate(`/app/crm/branch-tally/scan/${id}`);
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add participants");
    }
  };

  const breadcrumbs = [
    { title: "Branch Tally", path: listPath },
    { title: "Add Participants" },
  ];

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={isLoading}>
      <BreadcrumbLayout.Paper>
        <AppForm>
          <AppForm.Content>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <BaseAutocomplete
                  fullWidth
                  label="Branch"
                  options={tally?.branch ? [tally.branch] : []}
                  getOptionLabel={(opt) => opt?.name || ""}
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  value={tally?.branch ?? null}
                  onChange={() => {}}
                  disabled
                />
              </Grid>

              {(tally?.participants ?? []).length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Current Participants
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {tally.participants.map((p) => (
                      <Chip
                        key={p._id}
                        label={p.name}
                        size="small"
                        color={p.isCreator ? "primary" : "default"}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <BaseAutocomplete
                  multiple
                  fullWidth
                  label="Add Participants"
                  placeholder="Type at least 3 characters..."
                  options={availableUsers}
                  getOptionLabel={(opt) => opt?.name || ""}
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  value={newParticipants}
                  onChange={(_, v) => setNewParticipants(v)}
                  onInputChange={(_, v) => setUserSearch(v || "")}
                  noOptionsText={
                    userSearch.length < 3
                      ? "Type at least 3 characters..."
                      : "No users found"
                  }
                />
              </Grid>
            </Grid>
          </AppForm.Content>
          <AppForm.Actions
            paddingX={3}
            paddingY={2}
            gap={2}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button onClick={() => navigate(listPath)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || newParticipants.length === 0}
            >
              {saving ? "Saving..." : "Add Participants"}
            </Button>
          </AppForm.Actions>
        </AppForm>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default BranchTallyUpdateScreen;
